import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BIG_FIVE_TRAITS_LONG_NAME,
  CONTENT_KEY_BIG_FIVE,
  getBigFiveTraitResultCode,
  MODEL_TYPE_BIG_FIVE_FACETS,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";
import Loader from "../../components/loader";
import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";

import {
  getBigFivePersonalityBannerImage,
  getCookie,
  getPersonalityBannerImage,
} from "../../utils/utility-functions";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";

import {
  APP_NAVIGATION,
  BIG_FIVE_FACETS_CONTENT,
  LOCALE,
} from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";

const traitColors = {
  Openness: "blue",
  Conscientiousness: "green",
  Extraversion: "yellow",
  Agreeableness: "red",
  Neuroticism: "purple",
  O: "blue",
  C: "green",
  E: "yellow",
  A: "red",
  N: "purple",
};

const filterDataByTraitStart = (data, letter) => {
  return data.filter((item) => item.trait.title.startsWith(letter));
};

function splitAtSecondUnderscore(input) {
  const parts = input.split("_");
  if (parts.length < 3) return [input]; // Less than 2 underscores, return the whole string.
  return [
    parts.slice(0, 2).join("_"), // Join the first two parts with an underscore.
    parts.slice(2).join("_"), // Join the remaining parts.
  ];
}

// ------------- Basis of Trait code filter the content  REAL-------------
// const filterContentByTraitDetails = (content, traitDetails, traitKey) => {
//   if (!content || !traitDetails || !traitKey) return [];
//   const startWithTrait = traitDetails.filter((item) =>
//     item.startsWith(traitKey)
//   );
//   const results = startWithTrait
//     .map((key) => {
//       const [mainKey, subKey] = splitAtSecondUnderscore(key);
//       return content?.[BIG_FIVE_TRAITS_LONG_NAME[traitKey]]?.Content[mainKey]
//         ? content[BIG_FIVE_TRAITS_LONG_NAME[traitKey]].Content[mainKey][key]
//         : null;
//     })
//     .filter((item) => item !== null);
//   return results;
// };
const filterContentByTraitDetails = (content, traitDetails, traitKey) => {
  if (!content || !traitDetails || !traitKey) return [];

  const startWithTrait = traitDetails.filter((item) =>
    item.startsWith(traitKey)
  );

  const results = startWithTrait
    .map((key) => {
      const [mainKey, subKey] = splitAtSecondUnderscore(key);
      const flavourText =
        content?.[BIG_FIVE_TRAITS_LONG_NAME[traitKey]]?.FlavorText.desc;
      const rootHeading =
        content?.[BIG_FIVE_TRAITS_LONG_NAME[traitKey]]?.RootHeading;

      const contentForKey = content?.[BIG_FIVE_TRAITS_LONG_NAME[traitKey]]
        ?.Content[mainKey]
        ? content[BIG_FIVE_TRAITS_LONG_NAME[traitKey]].Content[mainKey][key]
        : null;

      // Return an object that includes both the content and the mainKey
      return contentForKey
        ? {
            content: contentForKey,
            mainKey,
            rootHeading,
            flavourText,
          }
        : null;
    })
    .filter((item) => item !== null);

  return results;
};

// --------------- Extract Trait Code Like This "C_A" ," A_A" --------------

const getTraitDetailsFromUserTraits = (userTraits) => {
  return (
    userTraits &&
    userTraits?.map((item) => {
      const { score, trait } = item;
      const { title } = trait; // Extracting title from the nested trait object

      return getBigFiveTraitResultCode(score, title);
    })
  );
};

const createSectionHeader = (title, description, gradientColors, imageSrc) => {
  return (
    <div
      className={`relative min-h-[24rem] sm:h-96 bg-gradient-to-r from-${gradientColors}-500 via-${gradientColors}-600 to-indigo-700 flex flex-col justify-center`}
    >
      {/* Background Image */}
      <img
        src={imageSrc}
        alt={title}
        className="absolute bottom-0 right-0 w-full md:w-2/3 h-auto md:h-full object-contain object-right"
      />

      {/* Overlay Gradient */}
      <div
        className={`absolute inset-0 bg-gradient-to-r from-${gradientColors}-500 via-${gradientColors}-600 to-transparent`}
      ></div>

      {/* Black Transparent Overlay */}
      <div className="absolute inset-0 bg-black opacity-10"></div>

      {/* Dotted Pattern Overlay */}
      <div className="absolute inset-0">
        <svg className="absolute h-full w-full" preserveAspectRatio="none">
          <pattern
            id="dots"
            x="0"
            y="0"
            width="20"
            height="20"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="2" cy="2" r="1" fill="rgba(255,255,255,0.1)" />
          </pattern>
          <rect width="100%" height="100%" fill="url(#dots)" />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 p-4 sm:p-8 lg:p-12 text-white flex flex-col justify-center items-start">
        <h2 className="text-3xl sm:text-5xl font-bold mb-4 sm:mb-6 text-white shadow-text">
          {title}
        </h2>
        <p className="text-lg sm:text-xl text-white shadow-text max-w-3xl">
          {description}
        </p>
      </div>
    </div>
  );
};

const HeroBanner = ({ title, description, color }) => {
  const trait = title;
  const traitColorClass = traitColors[trait] || color;
  return (
    <div
      className={`relative h-64 sm:h-96 bg-gradient-to-r from-${traitColorClass}-600 via-${traitColorClass}-500 to-indigo-600`}
    >
      <img
        src="https://insightfultraits.com/wp-content/uploads/2024/08/looking-ahead.png"
        alt={title}
        className="absolute bottom-0 right-0 w-full md:w-2/3 h-auto md:h-full object-cover object-right"
      />
      <div
        className={`absolute inset-0 bg-gradient-to-r from-${color}-500 via-${color}-600 to-indigo-700- to-transparent`}
      ></div>
      <div className="absolute inset-0 bg-black opacity-10"></div>
      <div className="relative h-full flex items-center">
        <div className="w-full px-8 py-12">
          <h2 className="text-4xl sm:text-6xl font-bold mb-6 text-white shadow-text">
            {title}
          </h2>
          <p className="text-xl sm:text-2xl text-white shadow-text max-w-3xl">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

const TraitVisualization = ({ traitData, keyInsights, t }) => {
  const traitName = traitData[0] && traitData[0]?.trait?.title.split("_")[0];
  const colorClass = traitColors[traitName] || "gray";

  return (
    <div className="mb-8">
      <div className="hidden bg-gradient-to-r from-yellow-500 via-yellow-600 text-yellow-600"></div>
      <h3 className="text-2xl font-bold text-gray-800 mb-4">
        {traitData[0]?.traitName}
        {/* {BIG_FIVE_TRAITS_LONG_NAME[traitName]} */}
      </h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        {/* Trait Summary */}
        <div className="space-y-4">
          {traitData.map((traitData) => {
            const traitTitle = traitData?.trait?.title.split("_")[0] || "";
            const traitColorClass = traitColors[traitTitle] || "gray";

            return (
              <div
                key={traitData.trait.id}
                className={`bg-${traitColorClass}-50 rounded-lg p-4`}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-gray-700">
                    {traitData?.fullName}
                  </span>
                  <span className={`text-sm text-${traitColorClass}-600`}>
                    {traitData.percentage}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`bg-${traitColorClass}-500 h-2 rounded-full transition-all duration-500`}
                    style={{ width: `${traitData.percentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
        {/* Key Insights */}
        <div className="bg-gray-50 rounded-xl p-4">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            {t("report.big_five_facets.key_insights")}
          </h3>
          <ul className="space-y-4">
            {keyInsights?.map((item) => (
              <li key={item.id} className="flex items-start">
                <span
                  className={`flex-shrink-0 w-2 h-2 rounded-full bg-${colorClass}-500 mt-2 mr-2`}
                ></span>
                <p className="text-gray-600">{item.split(".")[0] + "."}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const TraitSection = ({ data, imgUrl, t }) => {
  const traitName = data[0] && data[0]?.mainKey.split("_")[0];
  const traitColorClass = traitColors[traitName] || "gray";
  return (
    <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
      {createSectionHeader(
        data[0]?.rootHeading,
        data[0]?.flavourText,
        traitColorClass,
        imgUrl
      )}

      {/* <HeroBanner
        title={data[0]?.rootHeading}
        description={data[0]?.flavourText}
        color={traitColorClass}
      /> */}
      <div className="p-4 sm:p-10">
        <div className="space-y-6">
          {data?.length > 0 &&
            data.map((item, idx) => (
              <div
                key={idx}
                className={`bg-${traitColorClass}-50 p-4 rounded-lg shadow-md`}
              >
                <h3 className="text-2xl font-bold mb-4">
                  {item?.content?.Heading}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-xl font-semibold mb-2">
                      {" "}
                      {t("report.big_five_facets.strengths")}
                    </h4>
                    <ul className="list-disc pl-5 space-y-2">
                      {item?.content?.Pros.map((pro, index) => (
                        <li key={index} className="text-gray-700">
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-2">
                      {t("report.big_five_facets.challenges")}
                    </h4>
                    <ul className="list-disc pl-5 space-y-2">
                      {item?.content?.Cons.map((con, index) => (
                        <li key={index} className="text-gray-700">
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </section>
  );
};

const BigFiveFacetsReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  funnelStep,
  sendToFriendReportOnEmailConnect,
  getUserByIdConnect,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, gender, isAuth } = funnelStep || {};
  const userId = queryParams.get("uid") || userGUID;
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // const token = getCookie("jwtToken");
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );

        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_BIG_FIVE_FACETS[currentLocale] ||
            MODEL_TYPE_BIG_FIVE_FACETS[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });

        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId]);

  // Process all traits dynamically
  const getAllTraitDetails = (content, userTraits) => {
    const traitDetails = getTraitDetailsFromUserTraits(userTraits);

    // Iterate over BIG_FIVE_TRAITS_LONG_NAME keys
    const allTraitDetails = Object.keys(BIG_FIVE_TRAITS_LONG_NAME).reduce(
      (acc, traitKey) => {
        const matchedData = filterContentByTraitDetails(
          content,
          traitDetails,
          traitKey
        );
        return { ...acc, [traitKey]: matchedData };
      },
      {}
    );

    return allTraitDetails;
  };

  // UseMemo for efficient re-computation
  const matchedData = useMemo(() => {
    return getAllTraitDetails(contentData, userTraits);
  }, [contentData, userTraits]);

  const traitProgessBarDataByO = filterDataByTraitStart(userTraits, "O").map(
    (item) => {
      return {
        ...item,
        traitName: matchedData.O.filter((itm) =>
          item?.trait?.title?.includes(itm.mainKey)
        ) // Filter items matching the condition
          .map((itm) => itm.rootHeading) // Extract the Root Heading for matched items
          .join(", "), // Join all matched headings into a single string
        fullName: matchedData.O.filter((itm) =>
          item?.trait?.title?.includes(itm.mainKey)
        ) // Filter items matching the condition
          .map((itm) => itm.content.Heading) // Extract the Heading for matched items
          .join(", "), // Join all matched headings into a single string
      };
    }
  );

  const traitKeyInsightDataByO =
    contentData?.Openness?.Content &&
    Object?.values(contentData?.Openness?.Content).map(
      (entry) => entry.FlavorText
    );

  const traitProgessBarDataByC = filterDataByTraitStart(userTraits, "C").map(
    (item) => {
      return {
        ...item,
        traitName: matchedData.C.filter((itm) =>
          item?.trait?.title?.includes(itm.mainKey)
        ) // Filter items matching the condition
          .map((itm) => itm.rootHeading) // Extract the Root Heading for matched items
          .join(", "), // Join all matched headings into a single string
        fullName: matchedData.C.filter((itm) =>
          item?.trait?.title?.includes(itm.mainKey)
        ) // Filter items matching the condition
          .map((itm) => itm.content.Heading) // Extract the Heading for matched items
          .join(", "), // Join all matched headings into a single string
      };
    }
  );
  const traitKeyInsightDataByC =
    contentData?.Conscientiousness?.Content &&
    Object?.values(contentData?.Conscientiousness?.Content).map(
      (entry) => entry.FlavorText
    );

  const traitProgessBarDataByE = filterDataByTraitStart(userTraits, "E").map(
    (item) => {
      return {
        ...item,
        traitName: matchedData.E.filter((itm) =>
          item?.trait?.title?.includes(itm.mainKey)
        ) // Filter items matching the condition
          .map((itm) => itm.rootHeading) // Extract the Root Heading for matched items
          .join(", "), // Join all matched headings into a single string
        fullName: matchedData.E.filter((itm) =>
          item?.trait?.title?.includes(itm.mainKey)
        ) // Filter items matching the condition
          .map((itm) => itm.content.Heading) // Extract the Heading for matched items
          .join(", "), // Join all matched headings into a single string
      };
    }
  );
  const traitKeyInsightDataByE =
    contentData?.Extraversion?.Content &&
    Object?.values(contentData?.Extraversion?.Content).map(
      (entry) => entry.FlavorText
    );

  const traitProgessBarDataByA = filterDataByTraitStart(userTraits, "A").map(
    (item) => {
      return {
        ...item,
        traitName: matchedData.A.filter((itm) =>
          item?.trait?.title?.includes(itm.mainKey)
        ) // Filter items matching the condition
          .map((itm) => itm.rootHeading) // Extract the Root Heading for matched items
          .join(", "), // Join all matched headings into a single string
        fullName: matchedData.A.filter((itm) =>
          item?.trait?.title?.includes(itm.mainKey)
        ) // Filter items matching the condition
          .map((itm) => itm.content.Heading) // Extract the Heading for matched items
          .join(", "), // Join all matched headings into a single string
      };
    }
  );
  const traitKeyInsightDataByA =
    contentData?.Agreeableness?.Content &&
    Object?.values(contentData?.Agreeableness?.Content).map(
      (entry) => entry.FlavorText
    );

  const traitProgessBarDataByN = filterDataByTraitStart(userTraits, "N").map(
    (item) => {
      return {
        ...item,
        traitName: matchedData.N.filter((itm) =>
          item?.trait?.title?.includes(itm.mainKey)
        ) // Filter items matching the condition
          .map((itm) => itm.rootHeading) // Extract the Root Heading for matched items
          .join(", "), // Join all matched headings into a single string
        fullName: matchedData.N.filter((itm) =>
          item?.trait?.title?.includes(itm.mainKey)
        ) // Filter items matching the condition
          .map((itm) => itm.content.Heading) // Extract the Heading for matched items
          .join(", "), // Join all matched headings into a single string
      };
    }
  );
  const traitKeyInsightDataByN =
    contentData?.Neuroticism?.Content &&
    Object?.values(contentData?.Neuroticism?.Content).map(
      (entry) => entry.FlavorText
    );

  if (isLoading) {
    return <Loader isLoading={isLoading} />;
  }

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="container mx-auto p-4 sm:p-8">
        <section class="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <HeroBanner
            title={t("report.big_five_facets.big_five_facets_heading")}
            // description={contentData?.Overview?.split(".")[0]}
            color="purple"
          />
          {isAuth && (
            <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-4">
              <ShareButton
                currentAssessmentAttemptData={currentAssessmentAttemptData}
                userGUID={userId}
                userData={userData}
              />
              <SendToFriendButton
                currentAssessmentAttemptData={currentAssessmentAttemptData}
                userGUID={userGUID}
                userData={userData}
                CreateGuestUserConnect={sendToFriendReportOnEmailConnect}
              />
            </div>
          )}
          <div class="p-4 sm:p-10">
            <div class="max-w-auto mx-auto mb-12">
              <h2 class="text-3xl font-bold text-gray-800 mb-6">
                {contentData?.OverviewHeading || ""}
              </h2>
              <p class="text-lg text-gray-600 mb-4">
                {contentData.Overview || ""}
              </p>
            </div>

            {/* Trait Visualization  BY O */}
            <TraitVisualization
              traitData={traitProgessBarDataByO}
              keyInsights={traitKeyInsightDataByO}
              t={t}
            />

            {/* Trait Visualization  BY C */}
            <TraitVisualization
              traitData={traitProgessBarDataByC}
              keyInsights={traitKeyInsightDataByC}
              t={t}
            />

            {/* Trait Visualization  BY E */}
            <TraitVisualization
              traitData={traitProgessBarDataByE}
              keyInsights={traitKeyInsightDataByE}
              t={t}
            />

            {/* Trait Visualization  BY A */}
            <TraitVisualization
              traitData={traitProgessBarDataByA}
              keyInsights={traitKeyInsightDataByA}
              t={t}
            />
            {/* Trait Visualization  BY N */}
            <TraitVisualization
              traitData={traitProgessBarDataByN}
              keyInsights={traitKeyInsightDataByN}
              t={t}
            />
          </div>
        </section>

        <TraitSection
          data={matchedData?.O}
          imgUrl={getPersonalityBannerImage("interactions")}
          t={t}
        />
        <TraitSection
          data={matchedData?.C}
          imgUrl={getPersonalityBannerImage("role")}
          t={t}
        />
        <TraitSection
          data={matchedData?.E}
          imgUrl={getBigFivePersonalityBannerImage("environmental_traits")}
          t={t}
        />
        <TraitSection
          data={matchedData?.A}
          imgUrl={getPersonalityBannerImage("characteristics")}
          t={t}
        />
        <TraitSection
          data={matchedData?.N}
          imgUrl={getBigFivePersonalityBannerImage("big_five_overview")}
          t={t}
        />
        <section className="space-y-4">
          <div className="bg-purple-600 text-white rounded-xl p-6 text-center shadow-lg">
            <p className="text-xl font-bold mb-2">
              {contentData?.CTAPersonalityTest}
            </p>
            <button
              className="bg-white text-purple-600 px-6 py-2 rounded-full font-semibold hover:bg-purple-100 transition-colors"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("report.big_five_facets.discover_your_types_cta")}
            </button>
          </div>

          <div className="bg-blue-600 text-white hidden rounded-xl p-6 text-center shadow-lg">
            <p className="text-xl font-bold mb-2">{contentData?.CTAAITools}</p>
            <button className="bg-white text-blue-600 px-6 py-2 rounded-full font-semibold hover:bg-blue-100 transition-colors">
              {t("report.big_five_facets.explore_ai_tools")}
            </button>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default BigFiveFacetsReport;
